import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type {
  CreateModifierGroupRequest,
  CreateModifierRequest,
  ModifierDto,
  ModifierGroupDto,
  UpdateModifierGroupRequest,
  UpdateModifierRequest,
} from './types';

const QK_GROUPS = ['modifier-groups'] as const;

// ---- Modifier groups ----

export function useModifierGroups() {
  return useQuery({
    queryKey: QK_GROUPS,
    queryFn: async () =>
      (await apiClient.get<ModifierGroupDto[]>('/api/modifier-groups')).data,
  });
}

export function useCreateModifierGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateModifierGroupRequest) =>
      (await apiClient.post<ModifierGroupDto>('/api/modifier-groups', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_GROUPS }),
  });
}

export function useUpdateModifierGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateModifierGroupRequest }) =>
      (await apiClient.put<ModifierGroupDto>(`/api/modifier-groups/${id}`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_GROUPS }),
  });
}

export function useDeleteModifierGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/modifier-groups/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_GROUPS }),
  });
}

// ---- Modifiers (nested under a group) ----
// All modifier writes invalidate the parent list so the group row's
// modifier count and nested table stay in sync without a second fetch.

export function useCreateModifier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, request }: { groupId: string; request: CreateModifierRequest }) =>
      (await apiClient.post<ModifierDto>(
        `/api/modifier-groups/${groupId}/modifiers`,
        request,
      )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_GROUPS }),
  });
}

export function useUpdateModifier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      groupId,
      modifierId,
      request,
    }: {
      groupId: string;
      modifierId: string;
      request: UpdateModifierRequest;
    }) =>
      (await apiClient.put<ModifierDto>(
        `/api/modifier-groups/${groupId}/modifiers/${modifierId}`,
        request,
      )).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_GROUPS }),
  });
}

export function useDeleteModifier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ groupId, modifierId }: { groupId: string; modifierId: string }) => {
      await apiClient.delete(`/api/modifier-groups/${groupId}/modifiers/${modifierId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_GROUPS }),
  });
}
